import { CHARACTER_ACTIONS } from "@/character/actions";
import type { GridTuning } from "@/character/draft";
import {
  GRID_COLUMNS,
  resolveSheetLayout,
  sourceFrameRect,
  TEMPLATE_FRAME_HEIGHT,
  TEMPLATE_FRAME_WIDTH,
} from "@/character/grid";
import type { SheetUploadCleanPrefs } from "@/character/sheet-upload-clean";
import { applySheetUploadClean } from "@/character/sheet-upload-clean";
import type { PetFrameGrid } from "@/pet/compose-pet-frame-grid";
import { emptyPetFrameGrid } from "@/pet/compose-pet-frame-grid";

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("โหลดภาพไม่สำเร็จ"));
    img.src = dataUrl;
  });
}

function frameRectToDataUrl(
  image: HTMLImageElement,
  rect: { sx: number; sy: number; sw: number; sh: number },
  frameWidth: number,
  frameHeight: number,
): string {
  const canvas = document.createElement("canvas");
  canvas.width = frameWidth;
  canvas.height = frameHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("แยกเฟรมไม่สำเร็จ");
  ctx.clearRect(0, 0, frameWidth, frameHeight);
  const scale = Math.min(frameWidth / rect.sw, frameHeight / rect.sh);
  const dw = rect.sw * scale;
  const dh = rect.sh * scale;
  const dx = (frameWidth - dw) / 2;
  const dy = frameHeight - dh;
  ctx.drawImage(image, rect.sx, rect.sy, rect.sw, rect.sh, dx, dy, dw, dh);
  return canvas.toDataURL("image/png");
}

export async function extractPetFrameGridFromSheet(
  sheetDataUrl: string,
  tuning: GridTuning,
  cleanPrefs?: SheetUploadCleanPrefs,
): Promise<PetFrameGrid> {
  const image = await loadImage(sheetDataUrl);
  const layout = resolveSheetLayout(image, tuning);
  const grid = emptyPetFrameGrid();

  for (const action of CHARACTER_ACTIONS) {
    for (let col = 0; col < GRID_COLUMNS; col += 1) {
      const rect = sourceFrameRect(layout, col, action.row);
      let dataUrl = frameRectToDataUrl(
        image,
        rect,
        TEMPLATE_FRAME_WIDTH,
        TEMPLATE_FRAME_HEIGHT,
      );
      if (cleanPrefs) {
        dataUrl = await applySheetUploadClean(dataUrl, cleanPrefs);
      }
      grid[action.row][col] = dataUrl;
    }
  }

  return grid;
}
